/// <reference types="vite/client" />

import { HfInference } from "@huggingface/inference";
import { LatLng, NewsItem, Language, WeatherData, Alert } from "../types";

// --- INITIALIZATION ---
// Initialize Hugging Face Inference with the token from environment variables
const hf = new HfInference(import.meta.env.VITE_HF_TOKEN);

// Using a reliable instruction-tuned model from the free tier
const MODEL_ID = "meta-llama/Llama-3.2-3B-Instruct";

// --- WEATHER ANALYST ---
// Generate weather summary using smart templates (AI doesn't handle Arabic well)
export const generateWeatherSummary = async (weatherData: any, lang: Language): Promise<string> => {
    if (!weatherData.hourly) {
        return lang === 'ar' ? "لا تتوفر بيانات الطقس حالياً." : "Detailed weather data unavailable.";
    }

    const currentHour = new Date().getHours();
    const temps = weatherData.hourly.temperature_2m.slice(currentHour, currentHour + 6);
    const rain = weatherData.hourly.precipitation.slice(currentHour, currentHour + 6);
    const avgTemp = Math.round(temps.reduce((a: number, b: number) => a + b, 0) / temps.length);
    const maxTemp = Math.round(Math.max(...temps));
    const minTemp = Math.round(Math.min(...temps));
    const hasRain = rain.some((r: number) => r > 0);
    const maxRain = Math.max(...rain);
    const isHot = avgTemp > 35;
    const isCold = avgTemp < 10;
    const isMorning = currentHour < 12;
    const isEvening = currentHour >= 18;

    // Use smart templates instead of AI for Arabic (AI produces garbage)
    if (lang === 'ar') {
        // Build contextual Arabic summary
        let summary = '';

        // Temperature context
        if (isHot) {
            summary = `درجة الحرارة مرتفعة اليوم (${avgTemp}°). تجنب الخروج في أوقات الذروة واشرب الكثير من الماء. `;
        } else if (isCold) {
            summary = `الجو بارد اليوم (${avgTemp}°). تأكد من ارتداء ملابس دافئة. `;
        } else {
            summary = `الطقس معتدل اليوم (${avgTemp}°). `;
        }

        // Rain context
        if (hasRain && maxRain > 5) {
            summary += `توقع أمطار غزيرة. احمل مظلة وانتبه للطرق الزلقة.`;
        } else if (hasRain) {
            summary += `توقع أمطار خفيفة. يُفضل أخذ الحيطة أثناء القيادة.`;
        } else {
            if (isMorning) {
                summary += `يوم مناسب للخروج. استمتع بيومك!`;
            } else if (isEvening) {
                summary += `أمسية هادئة. الأجواء مناسبة للتنزه.`;
            } else {
                summary += `لا توجد أمطار متوقعة.`;
            }
        }

        return summary;
    }

    // English - can try AI with fallback
    try {
        const promptText = `You're a friendly AI assistant. Write a brief, conversational weather summary for today.
Current temperature: ${avgTemp}°C (high: ${maxTemp}°, low: ${minTemp}°)
Rain expected: ${hasRain ? 'Yes' : 'No'}${hasRain ? `, max ${maxRain}mm` : ''}
Time of day: ${isMorning ? 'Morning' : isEvening ? 'Evening' : 'Afternoon'}

Write 1-2 SHORT sentences like a friend giving weather advice. Be helpful but brief. No markdown, no emojis, just plain text.`;

        const response = await hf.chatCompletion({
            model: MODEL_ID,
            messages: [{ role: "user", content: promptText }],
            max_tokens: 80
        });

        const result = response.choices[0].message.content;
        // Validate result is clean English
        if (result && result.length > 10 && result.length < 200 && /^[a-zA-Z0-9\s.,!?'°-]+$/.test(result)) {
            return result;
        }
    } catch (e) {
        console.warn("AI summary failed, using template:", e);
    }

    // English fallback template
    let summary = `Today's temperature is ${avgTemp}°C. `;
    if (hasRain && maxRain > 5) {
        summary += `Heavy rain expected - bring an umbrella and drive carefully.`;
    } else if (hasRain) {
        summary += `Light rain possible. Roads may be slippery.`;
    } else if (isHot) {
        summary += `It's hot! Stay hydrated and avoid direct sun.`;
    } else if (isCold) {
        summary += `Bundle up - it's cold out there!`;
    } else {
        summary += `Great conditions for outdoor activities.`;
    }
    return summary;
};

// --- CONTEXT AWARE CHATBOT ---
interface AiContext {
    location?: string;
    weather?: WeatherData | null;
    hazards?: Alert[];
    squadStatus?: string;
}

export const generateSafetyAdvice = async (
    userQuery: string,
    history: { role: string, content: string }[],
    context: AiContext
): Promise<string> => {

    const contextBlock = `
  LIVE SITUATION DATA:
  - Location: ${context.location || "Unknown"}
  - Weather: ${context.weather ? `${context.weather.temp}°C, ${context.weather.condition}` : "Offline"}
  - Known Hazards: ${context.hazards?.length || 0}
  `;

    const systemInstruction = `
  You are 'Intel Cortex', the AI Guardian of the Protectly system.
  CORE DIRECTIVES:
  1. PERSONA: Capable, calm, tactical leader. Use emojis 🛡️ 📡.
  2. LIMITATIONS: You DO NOT have access to live real-time news search. Rely on general safety knowledge.
  3. FORMAT: No markdown bolding (**). Keep it clean.
  ${contextBlock}
  `;

    try {
        // Map history to HF format (ensure roles are user/assistant)
        const messages: any[] = [
            { role: "system", content: systemInstruction },
            ...history.map(h => ({
                role: h.role === 'model' ? 'assistant' : 'user',
                content: h.content
            })),
            { role: "user", content: userQuery }
        ];

        const response = await hf.chatCompletion({
            model: MODEL_ID,
            messages: messages,
            max_tokens: 300
        });

        return response.choices[0].message.content || "Systems interfering. Stand by.";

    } catch (e) {
        console.error("Cortex Error:", e);
        return "Neural link unstable. Rely on local protocols.";
    }
};

// --- DAILY TACTICAL BRIEFING ---
export const generateDailyTacticalBriefing = async (
    weather: any,
    hazards: Alert[],
    locationName: string
): Promise<string> => {
    const instruction = `
    You are the 'Tactical AI' for a user in ${locationName}.
    Analyze the daily data and output a ONE-SENTENCE strategic summary for the day.
    
    DATA:
    - Weather: ${JSON.stringify(weather)}
    - Active Hazards: ${hazards.length}
    
    STYLE:
    - Professional, military-grade brevity.
    - Focus on risk assessment and preparedness.
    - NO Markdown.
    
    Example: "Conditions nominal; expect light rain in the 1400 hours sector, standard patrol advised."
    `;

    try {
        const response = await hf.chatCompletion({
            model: MODEL_ID,
            messages: [{ role: "system", content: instruction }, { role: "user", content: "Report status." }],
            max_tokens: 100,
            temperature: 0.5
        });
        return response.choices[0].message.content || "Daily analysis nominal.";
    } catch (e) {
        return "Tactical feed offline. Proceed with standard caution.";
    }
};

// --- REAL-TIME NEWS AGENT (SIMULATED) ---
// Note: Free tier HF Inference does not support live web search tools directly in the browser without a proxy.
// We use Llama 3 to generate *realistic* safety incidents based on location/weather context to simulate a live feed.
export const fetchDisasterNews = async (
    location: LatLng,
    locationName: string,
    lang: Language = 'en',
    scope: 'local' | 'city' | 'country' | 'world' = 'local'
): Promise<NewsItem[]> => {

    // Helper for offsets based on scope
    const getCoordinates = (base: LatLng, s: string, offsetLat: number = 0, offsetLng: number = 0) => {
        if (s === 'world') {
            // Truly random global coordinates (avoiding extreme poles)
            return {
                lat: (Math.random() * 140) - 70,
                lng: (Math.random() * 360) - 180
            };
        }
        if (s === 'country') {
            // Wider spread (roughly +/- 500km)
            return {
                lat: base.lat + (Math.random() * 10 - 5),
                lng: base.lng + (Math.random() * 10 - 5)
            };
        }
        // Default Local/City
        return {
            lat: base.lat + (offsetLat || (Math.random() * 0.05 - 0.025)),
            lng: base.lng + (offsetLng || (Math.random() * 0.05 - 0.025))
        };
    };

    const instruction = `
    You are 'NetWatch', an advanced AI Tactical Scout.
    TASK: Scan simulated global/local networks for major safety and news events.
    
    CONTEXT:
    - Scope: ${scope.toUpperCase()}
    - Anchor Location: ${locationName}

    INSTRUCTIONS:
    - If Scope is LOCAL: Focus on ${locationName} streets/districts. (Traffic, Weather, Local Hazards)
    - If Scope is COUNTRY: Focus on major national cities/regions around ${locationName}.
    - If Scope is WORLD: generate MAJOR global events (e.g., 'Earthquake in Japan', 'Wildfire in California', 'Floods in Brazil').

    OUTPUT FORMAT:
    Return ONLY a JSON Array.
    [
        {
            "headline": "Headline (e.g., 'Magnitude 6.0 Quake')",
            "location": "City/Region Name",
            "category": "WEATHER/CIVIL/TRAFFIC/SYSTEM",
            "summary": "Brief tactical detail.",
            "reality_score": 9,
            "severity": "high" // critical, high, medium, low
        }
    ]
    `;

    try {
        const response = await hf.chatCompletion({
            model: MODEL_ID,
            messages: [
                { role: "system", content: instruction },
                { role: "user", content: `Generate 4 realistic events for scope: ${scope}.` }
            ],
            max_tokens: 600,
            temperature: 0.7
        });

        const jsonStr = response.choices[0].message.content || "[]";
        const start = jsonStr.indexOf('[');
        const end = jsonStr.lastIndexOf(']');
        const cleanJson = (start !== -1 && end !== -1) ? jsonStr.substring(start, end + 1) : "[]";
        const rawItems = JSON.parse(cleanJson);

        return rawItems.map((item: any, i: number) => ({
            id: `netwatch-${scope}-${Date.now()}-${i}`,
            headline: item.headline,
            source: "NetWatch Global",
            time: "Live Feed",
            category: (['WEATHER', 'CIVIL', 'TRAFFIC', 'SYSTEM'].includes(item.category) ? item.category : 'NEWS') as any,
            location: item.location || locationName,
            summary: item.summary,
            trustScore: (item.reality_score || 8) * 10,
            realityScore: item.reality_score || 8,
            severity: item.severity || 'medium',
            // Assign coords
            coordinates: getCoordinates(location, scope, item.lat_offset, item.lng_offset),
            url: "#",
            imageUrl: null
        }));

    } catch (e) {
        console.warn("HF/NetWatch Scrape Failed:", e);
        return [];
    }
};
